import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from './authService';

const BudgetOptions = ({ options, setOptions, commissionPercentage, itinerary }) => {
  const { token } = useAuth();
  const [availableAccommodations, setAvailableAccommodations] = useState([]);
  const [activeSearch, setActiveSearch] = useState(null); // { optionIndex, accIndex }

  useEffect(() => {
    const newOptions = options.map(option => {
        const accommodationTotal = option.accommodations?.reduce((sum, acc) => sum + (Number(acc.accomodationPrice) || 0), 0) || 0;
        
        const calculatedOptionPrice = 
            (Number(option.ticketsPrice) || 0) +
            (option.transfer ? (Number(option.transferPrice) || 0) : 0) +
            (option.carRental ? (Number(option.carRentalPrice) || 0) : 0) +
            (option.excursion ? (Number(option.excursionPrice) || 0) : 0) +
            (option.travelInsurance ? (Number(option.travelInsurancePrice) || 0) : 0) +
            accommodationTotal;

        const commissionAmount = calculatedOptionPrice * ((Number(commissionPercentage) || 0) / 100);
        const calculatedFinalPrice = calculatedOptionPrice + commissionAmount;

        if (option.optionPrice !== calculatedOptionPrice || option.finalPrice !== calculatedFinalPrice) {
            return {
                ...option,
                optionPrice: calculatedOptionPrice,
                finalPrice: calculatedFinalPrice
            };
        }
        return option;
    });

    if (JSON.stringify(newOptions) !== JSON.stringify(options)) {
        setOptions(newOptions);
    }
  }, [options, commissionPercentage, setOptions]);

  useEffect(() => {
    const fetchAccommodationsForItinerary = async () => {
        if (!itinerary || itinerary.length === 0 || !token) {
            setAvailableAccommodations([]);
            return;
        }
        
        const destinationIds = [...new Set(itinerary.map(item => item.destinationId).filter(Boolean))];
        
        if (destinationIds.length === 0) {
            setAvailableAccommodations([]);
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/acommodations?destinationId.in=${destinationIds.join(',')}&size=1000`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableAccommodations(data);
            } else {
                console.error('Failed to fetch accommodations for itinerary');
                setAvailableAccommodations([]);
            }
        } catch (error) {
            console.error('Error fetching accommodations:', error);
            setAvailableAccommodations([]);
        }
    };

    fetchAccommodationsForItinerary();
  }, [itinerary, token]);

  const addOption = () => {
    setOptions([...options, {
      typeTrip: '',
      travelCompany: '',
      luggage: '',
      origin: '',
      ticketsPrice: null,
      transfer: false,
      transferDescription: '',
      transferPrice: null,
      transferComments: '',
      carRental: false,
      carRentalDescription: '',
      carRentalPrice: null,
      carRentalComments: '',
      excursion: false,
      excursionDescription: '',
      excursionPrice: null,
      excursionComments: '',
      travelInsurance: false,
      travelInsuranceDescription: '',
      travelInsurancePrice: null,
      travelInsuranceComments: '',
      taxes: true,
      taxesComments: '',
      optionPrice: 0,
      finalPrice: 0,
      accommodations: []
    }]);
  };

  const removeOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setOptions(newOptions);
  };

  const addAccommodation = (optionIndex) => {
    const newOptions = [...options];
    if (!newOptions[optionIndex].accommodations) newOptions[optionIndex].accommodations = [];
    newOptions[optionIndex].accommodations.push({
      acommodation: null, // Will store the selected accommodation object
      searchTerm: '', // For the search input
      accomodationDescription: '',
      accomodationPrice: null,
      acommodationComments: ''
    });
    setOptions(newOptions);
  };

  const removeAccommodation = (optionIndex, accIndex) => {
    const newOptions = [...options];
    newOptions[optionIndex].accommodations = newOptions[optionIndex].accommodations.filter((_, i) => i !== accIndex);
    setOptions(newOptions);
  };

  const handleAccommodationChange = (optionIndex, accIndex, field, value) => {
    const newOptions = [...options];
    newOptions[optionIndex].accommodations[accIndex] = {
      ...newOptions[optionIndex].accommodations[accIndex],
      [field]: value
    };
    setOptions(newOptions);
  };

  const handleAccommodationSearchChange = (optionIndex, accIndex, value) => {
    const newOptions = [...options];
    const acc = newOptions[optionIndex].accommodations[accIndex];
    acc.searchTerm = value;
    acc.acommodation = null; // Deselect on manual change
    setOptions(newOptions);
  };

  const selectAccommodation = (optionIndex, accIndex, selectedAcc) => {
    const newOptions = [...options];
    const acc = newOptions[optionIndex].accommodations[accIndex];
    acc.acommodation = selectedAcc;
    acc.searchTerm = selectedAcc.acommodationName;
    // Pre-fill description from the selected accommodation
    acc.accomodationDescription = selectedAcc.acommodationDescription || '';
    
    setActiveSearch(null); // Close dropdown
    setOptions(newOptions);
  };

  return (
    <div style={styles.container}>
      <h4 style={styles.mainTitle}>Opciones de Presupuesto</h4>
      
      {options.map((option, index) => (
        <div key={index} style={styles.optionCard}>
          <div style={styles.cardHeader}>
            <h5 style={styles.optionTitle}>Opción {index + 1}</h5>
            <button type="button" onClick={() => removeOption(index)} style={styles.removeButton}>Eliminar Opción</button>
          </div>

          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Tipo de Viaje</label>
              <select value={option.typeTrip || ''} onChange={(e) => handleOptionChange(index, 'typeTrip', e.target.value)} style={styles.input}>
                <option value="">Seleccionar tipo</option>
                <option value="Aereos">Aereos</option>
                <option value="Terrestres">Terrestres</option>
                <option value="Maritimos">Maritimos</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Empresa</label>
              <input type="text" value={option.travelCompany} onChange={(e) => handleOptionChange(index, 'travelCompany', e.target.value)} style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Equipaje</label>
              <input type="text" value={option.luggage} onChange={(e) => handleOptionChange(index, 'luggage', e.target.value)} style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Origen</label>
              <input type="text" value={option.origin} onChange={(e) => handleOptionChange(index, 'origin', e.target.value)} style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Precio Tickets</label>
              <input type="number" value={option.ticketsPrice ?? ''} onChange={(e) => handleOptionChange(index, 'ticketsPrice', e.target.value === '' ? null : parseFloat(e.target.value))} style={styles.input} placeholder="Precio de los tickets" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Subtotal Opción</label>
              <input type="number" value={(option.optionPrice || 0).toFixed(2)} style={{...styles.input, backgroundColor: '#e9ecef'}} readOnly />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Precio Final (c/comisión)</label>
              <input type="number" value={(option.finalPrice || 0).toFixed(2)} style={{...styles.input, backgroundColor: '#e9ecef'}} readOnly />
            </div>
          </div>

          <div style={styles.checkboxGrid}>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" checked={option.transfer} onChange={(e) => handleOptionChange(index, 'transfer', e.target.checked)} style={styles.checkbox} />
                Transfer
              </label>
              {option.transfer && (
                <>
                  <label style={styles.fieldLabel}>Descripción <span style={styles.publicBadge}>Visible en PDF</span></label>
                  <textarea placeholder="Descripción Transfer" value={option.transferDescription || ''} onChange={(e) => handleOptionChange(index, 'transferDescription', e.target.value)} style={styles.textarea} />
                  <input type="number" placeholder="Precio Transfer" value={option.transferPrice ?? ''} onChange={(e) => handleOptionChange(index, 'transferPrice', e.target.value === '' ? null : parseFloat(e.target.value))} style={styles.subInput} />
                  <div style={styles.internalSection}>
                    <label style={styles.fieldLabel}>Comentarios <span style={styles.internalBadge}>Uso Interno</span></label>
                    <textarea placeholder="Comentarios internos sobre el transfer" value={option.transferComments || ''} onChange={(e) => handleOptionChange(index, 'transferComments', e.target.value)} style={{...styles.textarea, backgroundColor: '#f8f9fa'}} />
                  </div>
                </>
              )}
            </div>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" checked={option.carRental} onChange={(e) => handleOptionChange(index, 'carRental', e.target.checked)} style={styles.checkbox} />
                Alquiler Auto
              </label>
              {option.carRental && (
                <>
                  <label style={styles.fieldLabel}>Descripción <span style={styles.publicBadge}>Visible en PDF</span></label>
                  <textarea placeholder="Descripción Auto" value={option.carRentalDescription || ''} onChange={(e) => handleOptionChange(index, 'carRentalDescription', e.target.value)} style={styles.textarea} />
                  <input type="number" placeholder="Precio Alquiler" value={option.carRentalPrice ?? ''} onChange={(e) => handleOptionChange(index, 'carRentalPrice', e.target.value === '' ? null : parseFloat(e.target.value))} style={styles.subInput} />
                  <div style={styles.internalSection}>
                    <label style={styles.fieldLabel}>Comentarios <span style={styles.internalBadge}>Uso Interno</span></label>
                    <textarea placeholder="Comentarios internos sobre el auto" value={option.carRentalComments || ''} onChange={(e) => handleOptionChange(index, 'carRentalComments', e.target.value)} style={{...styles.textarea, backgroundColor: '#f8f9fa'}} />
                  </div>
                </>
              )}
            </div>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" checked={option.excursion} onChange={(e) => handleOptionChange(index, 'excursion', e.target.checked)} style={styles.checkbox} />
                Excursión
              </label>
              {option.excursion && (
                <>
                  <label style={styles.fieldLabel}>Descripción <span style={styles.publicBadge}>Visible en PDF</span></label>
                  <textarea placeholder="Descripción Excursión" value={option.excursionDescription || ''} onChange={(e) => handleOptionChange(index, 'excursionDescription', e.target.value)} style={styles.textarea} />
                  <input type="number" placeholder="Precio Excursión" value={option.excursionPrice ?? ''} onChange={(e) => handleOptionChange(index, 'excursionPrice', e.target.value === '' ? null : parseFloat(e.target.value))} style={styles.subInput} />
                  <div style={styles.internalSection}>
                    <label style={styles.fieldLabel}>Comentarios <span style={styles.internalBadge}>Uso Interno</span></label>
                    <textarea placeholder="Comentarios internos sobre la excursión" value={option.excursionComments || ''} onChange={(e) => handleOptionChange(index, 'excursionComments', e.target.value)} style={{...styles.textarea, backgroundColor: '#f8f9fa'}} />
                  </div>
                </>
              )}
            </div>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" checked={option.travelInsurance} onChange={(e) => handleOptionChange(index, 'travelInsurance', e.target.checked)} style={styles.checkbox} />
                Seguro Viaje
              </label>
              {option.travelInsurance && (
                <>
                  <label style={styles.fieldLabel}>Descripción <span style={styles.publicBadge}>Visible en PDF</span></label>
                  <textarea placeholder="Descripción Seguro" value={option.travelInsuranceDescription || ''} onChange={(e) => handleOptionChange(index, 'travelInsuranceDescription', e.target.value)} style={styles.textarea} />
                  <input type="number" placeholder="Precio Seguro" value={option.travelInsurancePrice ?? ''} onChange={(e) => handleOptionChange(index, 'travelInsurancePrice', e.target.value === '' ? null : parseFloat(e.target.value))} style={styles.subInput} />
                  <div style={styles.internalSection}>
                    <label style={styles.fieldLabel}>Comentarios <span style={styles.internalBadge}>Uso Interno</span></label>
                    <textarea placeholder="Comentarios internos sobre el seguro" value={option.travelInsuranceComments || ''} onChange={(e) => handleOptionChange(index, 'travelInsuranceComments', e.target.value)} style={{...styles.textarea, backgroundColor: '#f8f9fa'}} />
                  </div>
                </>
              )}
            </div>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" checked={option.taxes} onChange={(e) => handleOptionChange(index, 'taxes', e.target.checked)} style={styles.checkbox} />
                Impuestos Incluidos
              </label>
              {option.taxes && (
                <div style={styles.internalSection}>
                  <label style={styles.fieldLabel}>Comentarios <span style={styles.internalBadge}>Uso Interno</span></label>
                  <textarea placeholder="Comentarios internos sobre impuestos" value={option.taxesComments || ''} onChange={(e) => handleOptionChange(index, 'taxesComments', e.target.value)} style={{...styles.textarea, backgroundColor: '#f8f9fa'}} />
                </div>
              )}
            </div>
          </div>

          <div style={styles.accommodationsSection}>
            <h6 style={styles.subTitle}>Alojamientos</h6>
            {option.accommodations && option.accommodations.map((acc, accIndex) => (
              <div key={accIndex} style={styles.accommodationRow}>
                <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
                  <label style={styles.fieldLabel}>Hotel</label>
                  <input
                    type="text"
                    placeholder="Buscar Alojamiento..."
                    value={acc.searchTerm}
                    onChange={(e) => handleAccommodationSearchChange(index, accIndex, e.target.value)}
                    onFocus={() => setActiveSearch({ optionIndex: index, accIndex })}
                    onBlur={() => setTimeout(() => setActiveSearch(null), 200)} // Delay to allow click on suggestion
                    style={styles.accInput}
                    autoComplete="off"
                  />
                  {activeSearch && activeSearch.optionIndex === index && activeSearch.accIndex === accIndex && (
                    <div style={styles.suggestionsDropdown}>
                      {(availableAccommodations.filter(availAcc => 
                          acc.searchTerm ? availAcc.acommodationName.toLowerCase().includes(acc.searchTerm.toLowerCase()) : true
                        ).length > 0) ? (
                          availableAccommodations
                            .filter(availAcc => acc.searchTerm ? availAcc.acommodationName.toLowerCase().includes(acc.searchTerm.toLowerCase()) : true)
                            .slice(0, 10) // Show top 10 results
                            .map(suggestion => (
                              <div key={suggestion.id} style={styles.suggestionItem} onClick={() => selectAccommodation(index, accIndex, suggestion)}>
                                {suggestion.acommodationName}
                              </div>
                            ))) : (<div style={styles.suggestionItem}>No hay alojamientos para los destinos del itinerario.</div>)
                      }
                    </div>
                  )}
                </div>
                <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', minWidth: '200px' }}>
                  <label style={styles.fieldLabel}>Descripción <span style={styles.publicBadge}>Visible en PDF</span></label>
                  <textarea placeholder="Descripción del hotel..." value={acc.accomodationDescription} onChange={(e) => handleAccommodationChange(index, accIndex, 'accomodationDescription', e.target.value)} style={{...styles.accInput, minHeight: '35px', resize: 'vertical', fontFamily: 'inherit', width: '100%'}} />
                </div>
                <div style={{ width: '120px', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                  <label style={styles.fieldLabel}>Precio</label>
                  <input type="number" placeholder="0" value={acc.accomodationPrice ?? ''} onChange={(e) => handleAccommodationChange(index, accIndex, 'accomodationPrice', e.target.value === '' ? null : parseFloat(e.target.value))} style={styles.accInput} />
                </div>
                <div style={{ flex: 1.5, ...styles.internalSection, marginTop: 0, display: 'flex', flexDirection: 'column', minWidth: '200px' }}>
                  <label style={styles.fieldLabel}>Comentarios <span style={styles.internalBadge}>Uso Interno</span></label>
                  <textarea placeholder="Comentarios internos..." value={acc.acommodationComments || ''} onChange={(e) => handleAccommodationChange(index, accIndex, 'acommodationComments', e.target.value)} style={{...styles.accInput, minHeight: '35px', resize: 'vertical', fontFamily: 'inherit', backgroundColor: '#f8f9fa', width: '100%'}} />
                </div>
                <button type="button" onClick={() => removeAccommodation(index, accIndex)} style={{...styles.removeAccButton, marginTop: '22px'}}>×</button>
              </div>
            ))}
            <button type="button" onClick={() => addAccommodation(index)} style={styles.addAccButton}>+ Agregar Alojamiento</button>
          </div>
        </div>
      ))}

      <button type="button" onClick={addOption} style={styles.addButton}>+ Agregar Opción de Presupuesto</button>
    </div>
  );
};

const styles = {
  container: { marginBottom: '30px', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeeba' },
  mainTitle: { fontSize: '20px', fontWeight: '600', color: '#856404', marginBottom: '15px', borderBottom: '2px solid #ffc107', paddingBottom: '5px' },
  optionCard: { backgroundColor: 'white', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #dee2e6', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' },
  optionTitle: { margin: 0, fontSize: '18px', color: '#333' },
  removeButton: { backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' },
  field: { display: 'flex', flexDirection: 'column' },
  fieldLabel: { fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '2px' },
  internalSection: { backgroundColor: '#fdfdfd', padding: '8px', borderRadius: '6px', marginTop: '5px' },
  internalBadge: { fontSize: '10px', color: '#6c757d', backgroundColor: '#e9ecef', padding: '1px 4px', borderRadius: '3px', marginLeft: '5px', fontWeight: 'normal' },
  publicBadge: { fontSize: '10px', color: '#28a745', backgroundColor: '#d4edda', padding: '1px 4px', borderRadius: '3px', marginLeft: '5px', fontWeight: 'normal' },
  label: { fontSize: '14px', fontWeight: 'bold', color: '#495057', marginBottom: '4px' },
  input: { padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' },
  checkboxGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '15px' },
  checkboxGroup: { display: 'flex', flexDirection: 'column' },
  checkboxLabel: { display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer', marginBottom: '5px' },
  checkbox: { marginRight: '8px' },
  subInput: { padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', marginTop: '5px' },
  textarea: { padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', minHeight: '60px', resize: 'vertical', width: '100%', marginTop: '5px', fontFamily: 'inherit' },
  accommodationsSection: { backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '6px' },
  subTitle: { margin: '0 0 10px 0', fontSize: '16px', color: '#495057' },
  accommodationRow: { display: 'flex', gap: '15px', marginBottom: '15px', alignItems: 'flex-start' },
  accInput: { padding: '6px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', width: '100%' },
  removeAccButton: { backgroundColor: '#dc3545', color: 'white', border: 'none', width: '24px', height: '24px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'center' },
  addAccButton: { backgroundColor: '#28a745', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },
  addButton: { backgroundColor: '#ffc107', color: '#212529', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', fontWeight: '500' },
  suggestionsDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '150px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  suggestionItem: { padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f1f1', fontSize: '14px' },
};

export default BudgetOptions;